import { EMOTE_NAMES } from '../generated/emotes';
import { FLAG_CODES } from '../generated/flags';
import { flagSvgPath } from '../services/Flags';

/**
 * Asset Warmup — idle-time prefetch of lazily-referenced UI assets.
 *
 * The Pixi bundles (game sprites, maps) load during boot, but the UI layer
 * references hundreds of loose files ad-hoc: flag SVGs and emote PNGs the
 * first time their panel opens, gameplay/notice art the first time an event
 * fires. On a cold HTTP cache that first use pays the network round-trip at
 * the worst possible moment (mid-game, mid-animation) — the CTF victory
 * banner used to visibly snap-center for exactly this reason.
 *
 * Warming is `new Image().src`: it primes the HTTP cache and fails silently;
 * no reference needs to survive. Two tiers:
 *
 * - Event art (~a dozen files): warmed immediately. These appear mid-game
 *   without user action, where a cold-cache flash is most jarring.
 * - Flags (276 SVGs) + emotes (144 PNGs), ~3MB total: trickled in small
 *   batches through requestIdleCallback so the warmup never contends with
 *   boot, login, or gameplay traffic. First panel open then hits a warm
 *   cache. Skipped entirely when the browser signals Save-Data.
 *
 * Note this only removes NETWORK cost. If a panel janks on its second open
 * in a session (warm cache), the cost is DOM mount/rasterization of hundreds
 * of nodes — a different problem with a different fix (keep-mounted DOM,
 * content-visibility).
 */

/** Mid-game event art fetched outside any user-opened panel: the match
 *  results, the level-up notice, the spectator eye, the chat's controls, the
 *  game log's join, leave and flag glyphs, and the leaderboard's crown. Every
 *  other UI image is on screen from the game's first frame or behind a panel
 *  the player opens. */
const EVENT_ART = [
  '/vanilla/ui/gameplay/ctfvictory.png',
  '/vanilla/ui/gameplay/ctfdefeat.png',
  '/vanilla/ui/gameplay/ctf_flag_blue.png',
  '/vanilla/ui/gameplay/ctf_flag_red.png',
  '/vanilla/ui/gameplay/eye.svg',
  '/vanilla/ui/gameplay/level-badge.svg',
  '/vanilla/ui/gameplay/level-trophy.svg',
  '/vanilla/ui/chat/chat-minimized.svg',
  '/vanilla/ui/chat/eraser.svg',
  '/vanilla/ui/chat/minus.svg',
  '/vanilla/ui/chat/move-diagonal.svg',
  '/vanilla/ui/chat/x.svg',
  '/vanilla/ui/log/log-in.svg',
  '/vanilla/ui/log/log-out.svg',
  '/vanilla/ui/scoreboard/flag.svg',
  '/vanilla/ui/scoreboard/crown.svg',
];

/** Requests created per idle slice — small enough to stay off the network's
 *  critical path, large enough to finish the ~420-file tier in seconds. */
const BATCH_SIZE = 16;

function warm(urls: readonly string[]) {
  for (const url of urls) {
    new Image().src = url;
  }
}

/** Schedule the next batch when the main thread is idle. Safari has no
 *  requestIdleCallback; a short timeout is close enough for fire-and-forget
 *  cache priming. */
function whenIdle(callback: () => void) {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(callback, { timeout: 2000 });
  } else {
    window.setTimeout(callback, 250);
  }
}

function warmInIdleBatches(urls: string[], offset = 0) {
  if (offset >= urls.length) return;
  whenIdle(() => {
    warm(urls.slice(offset, offset + BATCH_SIZE));
    warmInIdleBatches(urls, offset + BATCH_SIZE);
  });
}

/** Kick off the warmup. Called once at the end of boot, after the Pixi
 *  bundles and games-data fetches have had the network to themselves. */
export function startAssetWarmup() {
  // Respect an explicit data-saving preference (Chromium-only signal; the
  // narrow cast keeps us off `any` for a property TS's lib doesn't model).
  const { connection } = navigator as Navigator & { connection?: { saveData?: boolean } };
  if (connection?.saveData === true) return;

  warm(EVENT_ART);

  const bulk = [
    ...FLAG_CODES.map((code) => flagSvgPath(code)),
    ...EMOTE_NAMES.map((name) => `/vanilla/ui/emotes/emote_${name}.png`),
  ];
  warmInIdleBatches(bulk);
}
